import React, { useCallback, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Divider } from '@mui/material';
import {
  DigitalQuestion,
  DigitalSubmission,
  DigitalGroundTruth,
  DigitalSubmissionNumberRange,
  QuestionTypeV2,
  PartialBy,
  DigitalSubmissionSingleChoice,
  DigitalSubmissionMultiChoice,
  DigitalSubmissionString,
  DigitalSubmissionRatio,
  DigitalSubmissionChoiceWithText,
} from '../../redux/types';
import ApiClient from '../../loopback/apiClient';
import { DigitalSubmissionRequest } from '../../loopback/apiClient/DRTApi';
import RevealedAnswers from '../digitalTrial/RevealedAnswers';
import NumberRangeQuestion from './numberRange/NumberRangeQuestion';
import SingleChoiceQuestion from './singleChoice/SingleChoiceQuestion';
import MultiChoiceQuestion from './multiChoice/MultiChoiceQuestion';
import StringQuestion from './string/StringQuestion';
import RatioQuestion from './ratio/RatioQuestion';
import SingleChoiceWithTextQuestion from './singleChoiceWithText/SingleChoiceWithTextQuestion';
import MultiChoiceWithTextQuestion from './multiChoiceWithText/MultiChoiceWithTextQuestion';

type Props = {
  digitalQuestions: DigitalQuestion[];
  digitalCaseId: number;
  digitalSectionId: number;
  trialId: number;
  enrollmentId: number;
  disabled: boolean;
  submissionsMapByQuestionAndCaseId: Record<
    number,
    Record<number, DigitalSubmission> | undefined
  >;
  addOrUpdateDigitalSubmission: (submission: DigitalSubmission) => void;
  exerciseGroundTruth?: Record<
    number,
    Record<number, DigitalGroundTruth> | undefined
  >;
  setHideAnnotation: React.Dispatch<React.SetStateAction<boolean>>;
  hideActionButtons?: boolean;
};

const QuestionInputContainer: React.FC<Props> = ({
  digitalQuestions,
  digitalCaseId,
  digitalSectionId,
  trialId,
  enrollmentId,
  disabled,
  submissionsMapByQuestionAndCaseId,
  addOrUpdateDigitalSubmission,
  exerciseGroundTruth,
  setHideAnnotation,
  hideActionButtons,
}: Props) => {
  const navigate = useNavigate();
  const [revealAnswers, setRevealAnswers] = useState<boolean>(false);

  useEffect(() => {
    setRevealAnswers(false);
    setHideAnnotation(true);
  }, [digitalCaseId, setHideAnnotation]);

  const onRevealAnswer = () => {
    setRevealAnswers(!revealAnswers);
    setHideAnnotation(!!revealAnswers);
  };

  const onHomeClick = () => {
    navigate(`/digitalTrial/${trialId}/menu`);
  };

  const onSaveHandler = useCallback(
    async (val?: PartialBy<DigitalSubmission, 'id'>) => {
      if (val === undefined) {
        return;
      }
      const reqPayload: DigitalSubmissionRequest = {
        answer: val.answer,
        digitalQuestionId: val.digitalQuestionId,
        digitalSectionId: val.digitalSectionId,
        digitalCaseId: val.digitalCaseId,
        type: val.type,
        trialId,
      };
      const savedSubmission = await ApiClient.addOrUpdateDigitalSubmission(
        reqPayload,
      );
      addOrUpdateDigitalSubmission(savedSubmission);
    },
    [addOrUpdateDigitalSubmission, trialId],
  );

  return (
    <Box>
      {digitalQuestions
        && digitalQuestions.map((dq) => (
          <Box key={dq.id}>
            <Divider light variant="middle" />
            <Box
              aria-label="input-container"
              display="flex"
              gap={6}
              alignItems="center"
            >
              <Box flexGrow={1} sx={{ my: 6 }}>
                {dq.type === QuestionTypeV2.NumberRange && (
                  <NumberRangeQuestion
                    mode="submit"
                    caseId={digitalCaseId}
                    question={dq}
                    sectionId={digitalSectionId}
                    enrollmentId={enrollmentId}
                    onSave={onSaveHandler}
                    defaultSubmission={
                      submissionsMapByQuestionAndCaseId[dq.id]?.[
                        digitalCaseId
                      ] as DigitalSubmissionNumberRange
                    }
                    disabled={disabled}
                  />
                )}
                {dq.type === QuestionTypeV2.SingleChoice && (
                  <SingleChoiceQuestion
                    mode="submit"
                    caseId={digitalCaseId}
                    question={dq}
                    sectionId={digitalSectionId}
                    enrollmentId={enrollmentId}
                    onSave={onSaveHandler}
                    defaultSubmission={
                      submissionsMapByQuestionAndCaseId[dq.id]?.[
                        digitalCaseId
                      ] as DigitalSubmissionSingleChoice
                    }
                    disabled={disabled}
                  />
                )}
                {dq.type === QuestionTypeV2.MultiChoice && (
                  <MultiChoiceQuestion
                    mode="submit"
                    caseId={digitalCaseId}
                    sectionId={digitalSectionId}
                    enrollmentId={enrollmentId}
                    question={dq}
                    onSave={onSaveHandler}
                    defaultSubmission={
                      submissionsMapByQuestionAndCaseId[dq.id]?.[
                        digitalCaseId
                      ] as DigitalSubmissionMultiChoice
                    }
                    disabled={disabled}
                  />
                )}
                {dq.type === QuestionTypeV2.String && (
                  <StringQuestion
                    mode="submit"
                    caseId={digitalCaseId}
                    sectionId={digitalSectionId}
                    enrollmentId={enrollmentId}
                    question={dq}
                    onSave={onSaveHandler}
                    defaultSubmission={
                      submissionsMapByQuestionAndCaseId[dq.id]?.[
                        digitalCaseId
                      ] as DigitalSubmissionString
                    }
                    disabled={disabled}
                  />
                )}
                {dq.type === QuestionTypeV2.Ratio && (
                  <RatioQuestion
                    mode="submit"
                    caseId={digitalCaseId}
                    sectionId={digitalSectionId}
                    enrollmentId={enrollmentId}
                    question={dq}
                    onSave={onSaveHandler}
                    defaultSubmission={
                      submissionsMapByQuestionAndCaseId[dq.id]?.[
                        digitalCaseId
                      ] as DigitalSubmissionRatio
                    }
                    disabled={disabled}
                  />
                )}
                {dq.type === QuestionTypeV2.SingleChoiceWithText && (
                  <SingleChoiceWithTextQuestion
                    mode="submit"
                    caseId={digitalCaseId}
                    sectionId={digitalSectionId}
                    enrollmentId={enrollmentId}
                    question={dq}
                    onSave={onSaveHandler}
                    defaultSubmission={
                      submissionsMapByQuestionAndCaseId[dq.id]?.[
                        digitalCaseId
                      ] as DigitalSubmissionChoiceWithText<
                        QuestionTypeV2.SingleChoiceWithText
                      >
                    }
                    disabled={disabled}
                  />
                )}
                {dq.type === QuestionTypeV2.MultiChoiceWithText && (
                  <MultiChoiceWithTextQuestion
                    mode="submit"
                    caseId={digitalCaseId}
                    sectionId={digitalSectionId}
                    enrollmentId={enrollmentId}
                    question={dq}
                    onSave={onSaveHandler}
                    defaultSubmission={
                      submissionsMapByQuestionAndCaseId[dq.id]?.[
                        digitalCaseId
                      ] as DigitalSubmissionChoiceWithText<
                        QuestionTypeV2.MultiChoiceWithText
                      >
                    }
                    disabled={disabled}
                  />
                )}
              </Box>
              {revealAnswers && exerciseGroundTruth && (
                <Box minWidth={400}>
                  <RevealedAnswers
                    caseId={digitalCaseId}
                    question={dq}
                    exerciseGroundTruthMapByQuestionAndCase={
                      exerciseGroundTruth
                    }
                    submissionsMapByQuestionAndCaseId={
                      submissionsMapByQuestionAndCaseId
                    }
                  />
                </Box>
              )}
            </Box>
          </Box>
        ))}
      {exerciseGroundTruth && !hideActionButtons && (
        <Box
          display="flex"
          justifyContent="flex-start"
          aria-label="survey actions"
        >
          <Button
            sx={{ m: 1 }}
            color="tertiary"
            onClick={onRevealAnswer}
            variant="contained"
          >
            {revealAnswers ? 'Hide Answers' : 'Reveal Answers'}
          </Button>
          <Button sx={{ m: 1 }} variant="outlined" onClick={onHomeClick}>
            Home
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default QuestionInputContainer;
